import * as yup from 'yup';
import moment from 'moment';
import i18n from 'i18next';
import BigNumber from 'bignumber.js';
import { toNumber } from 'lodash';

import {
  AppModule,
  DateFormat,
  InputType,
  ValidationPatterns,
  YupType
} from './constants';
import {
  CodebookInterface,
  InputOption,
  KeyedObject,
  RadioInputOption,
  ServiceLevelSpecificationInterface,
  UserGroupInterface,
  ValidityType
} from 'models';
import { CustomControlProps, FormInput } from 'models/form';
import { findCharacteristicSpecificationByName } from 'utils/characteristic';
import {
  BalanceGroupInterface,
  DeliveryPointInterface
} from 'features/eai/eaiTypes';
import { CharacteristicSpecificationInterface } from 'models/characteristics';

// ==============================|| UTILS ||============================== //

export const capitalizeFirstLetter = (inputString: string): string =>
  inputString.charAt(0).toUpperCase() + inputString.slice(1);

export const isEmptyObject = (object: object): boolean => {
  if (!object) return true;

  for (const _key in object) return false;

  return true;
};

export const isValidJson = (jsonString: string): boolean => {
  try {
    JSON.parse(jsonString);
    return true;
  } catch (error) {
    return false;
  }
};

const translationFromPath = (
  i18nPath: string,
  fallbackValue: string
): string => {
  const foundTranslation = getTranslationStringFromPath(i18nPath);
  return foundTranslation ?? fallbackValue;
};

export const getTranslationStringFromPath = (
  i18nKeyPath: string
): string | undefined => {
  if (!i18n.exists(i18nKeyPath)) {
    return undefined;
  }
  /*
   - when value is an object (nested translations), t function does not throw an error, but returns error message "key returned an object instead of string"
  */
  const resolvedValue = i18n.t(i18nKeyPath, {
    returnObjects: true
  });
  if (typeof resolvedValue === 'string') {
    return resolvedValue as string;
  }
  return undefined;
};

export const i18nValue = (i18nPath: string, value?: string): string => {
  return value ? translationFromPath(i18nPath, value) : '';
};

export const findKeyByValue = (
  obj: Record<string, string[]>,
  value: string
): string | null => {
  for (const key in obj) {
    if (
      Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key].includes(value)
    ) {
      return key;
    }
  }

  return null;
};

export const hasTheSameKey = (object1: object, object2: object): boolean => {
  for (const key in object1) {
    if (Object.prototype.hasOwnProperty.call(object2, key)) return true;
  }
  return false;
};

export const createValidationSchema = (
  inputs: FormInput[],
  valueCharacteristics?: CharacteristicSpecificationInterface[]
): yup.ObjectSchema<any> => {
  const validationSchema: KeyedObject = {};

  inputs?.forEach((input) => {
    const { id, validation } = input;

    if (validation) {
      const { type, methods } = validation;

      if (!!type && Object.values(YupType).some((value) => value === type)) {
        let inputSchema: any = yup[type]();

        if (methods && Array.isArray(methods)) {
          for (const method of methods) {
            const { methodType, args } = method;
            if (methodType)
              inputSchema = inputSchema[methodType](...(args ?? []));
          }
        }

        if (valueCharacteristics) {
          inputSchema['valueCharacteristic'] =
            findCharacteristicSpecificationByName(valueCharacteristics, id);
        }

        validationSchema[id] = inputSchema;
      }
    }
  });

  return yup.object(validationSchema);
};

const getOptions = (
  type: string,
  module: AppModule,
  name: string,
  codebooks: CodebookInterface[],
  serviceLevelSpecification: ServiceLevelSpecificationInterface[] | undefined
) => {
  if (type === InputType.RADIO_GROUP) {
    return createFormControlRadioOptions(
      module,
      name,
      codebooks,
      serviceLevelSpecification
    );
  }

  if (type === InputType.SELECT) {
    return createFormControlSelectOptions(module, name, codebooks);
  }

  return [];
};

export const createFormControlProps = ({
  name,
  type,
  touched,
  errors,
  codebooks,
  module,
  entityRoot,
  disabled,
  serviceLevelSpecification,
  options,
  isMultiline,
  endAdornment,
  maxLength,
  disableAjaxDisabling
}: CustomControlProps) => {
  return {
    captionLabel: `${entityRoot}.input.${name}`,
    errorMessage:
      touched[name] && errors[name]
        ? `${entityRoot}.validation.${errors[name]}`
        : '',
    options:
      options ??
      getOptions(type, module, name, codebooks, serviceLevelSpecification),
    disabled: disabled ?? false,
    isMultiline: isMultiline ?? false,
    endAdornment: endAdornment ?? undefined,
    maxLength: maxLength,
    disableAjaxDisabling: disableAjaxDisabling ?? false
  };
};

export const createFormControlSelectOptions = (
  module: AppModule,
  name: string,
  codebooks: CodebookInterface[] | undefined
): InputOption[] => {
  return (
    getCodebook(codebooks || [], name)?.values.map((value: string) => {
      const labelBase = (val?: string) =>
        `module.${module}.codebooks.${val ?? value}`;

      return {
        label: i18nValue(`${labelBase()}.title`, value),
        value: value
      };
    }) ?? []
  );
};

export const createFormControlRadioOptions = (
  module: AppModule,
  name: string,
  codebooks: CodebookInterface[] | undefined,
  serviceLevelSpecification?: ServiceLevelSpecificationInterface[]
): RadioInputOption[] => {
  return (
    getCodebook(codebooks || [], name)?.values.map((value: string) => {
      const labelBase = (val?: string) =>
        `module.${module}.codebooks.${val ?? value}`;

      const services: string[] | undefined = serviceLevelSpecification
        ?.find((item) => item.serviceLevelCode === value)
        ?.services.map((service) => `${labelBase(service)}.title`);

      let caption: string[] = [];

      if (i18n.exists([`${labelBase()}.caption`])) {
        caption.push(`${labelBase()}.caption`);
      }

      if (services) caption = [...caption, ...services];

      return {
        title: `${labelBase()}.title`,
        caption: caption,
        value: value
      };
    }) ?? []
  );
};

const getCodebook = (
  codebooks: CodebookInterface[],
  name: string | undefined
): CodebookInterface | undefined => {
  return codebooks.find((item) => item.name === name);
};

export const sortByValidFrom = <T extends { validFor: ValidityType }>(
  collection: T[]
): T[] => {
  const sortedCollection = [...collection];

  sortedCollection.sort((a, b) => {
    const dateA = moment(a.validFor.validFromDate);
    const dateB = moment(b.validFor.validFromDate);

    return moment(dateB).diff(dateA);
  });

  return sortedCollection;
};

export const getCurrentItem = <T extends { validFor: ValidityType }>(
  collection: T[]
): T | undefined => {
  if (collection.length === 0) {
    return undefined;
  }

  let currentValidFor: string = moment(
    collection[0].validFor.validFromDate
  ).format(DateFormat.REST_DATETIME_FORMAT);
  let currentItem: T = collection[0];

  for (const item of collection) {
    const itemValidFor: string = moment(item.validFor.validFromDate).format(
      DateFormat.REST_DATETIME_FORMAT
    );
    if (moment(itemValidFor).isAfter(currentValidFor)) {
      currentValidFor = itemValidFor;
      currentItem = item;
    }
  }

  return currentItem;
};

export const getServiceLevelCode = (
  serviceLevelSpecification: ServiceLevelSpecificationInterface[],
  serviceLevelSpecificationId: string
): string =>
  serviceLevelSpecification.find(
    (item) => item.id === serviceLevelSpecificationId
  )?.serviceLevelCode ?? '';

export const createBalanceGroupsSelectOptions = (
  balanceGroups: BalanceGroupInterface[] | undefined
): InputOption[] => {
  if (!balanceGroups || balanceGroups.length === 0) {
    return [];
  }

  return balanceGroups.map((balanceGroup) => {
    return {
      label: balanceGroup.name,
      value: balanceGroup.id.toString()
    };
  });
};

export const createDeliveryPointSelectOptions = (
  deliveryPointList: DeliveryPointInterface[]
) => {
  return deliveryPointList.map((deliveryPoint) => {
    return {
      label: deliveryPoint.name,
      value: deliveryPoint.id.toString()
    };
  });
};

export const parseToNumberWithDot = (value: string | undefined): number => {
  return toNumber(value?.replaceAll(',', '.'));
};

export const getUserGroupNamesLocale = (
  userGroupNames: string[] | undefined,
  userGroups: UserGroupInterface[],
  locale: string
): string => {
  const userGroupNamesLocale = userGroupNames?.map((userGroupName) => {
    const userGroup = userGroups?.find(
      (userGroupItem: UserGroupInterface) =>
        userGroupItem.userGroupName === userGroupName
    );

    return userGroup
      ? userGroup.attributes?.find(
          (attribute) => attribute.name === `displayName-${locale}`
        )?.value || userGroupName
      : '';
  });

  return (
    userGroupNamesLocale
      ?.filter((userGroupNameLocale) => userGroupNameLocale)
      .join(', ') || ''
  );
};

export const SAFE_CHARACTERS_LENGTH_FOR_PLANNING =
  Number.MAX_SAFE_INTEGER.toString().length - 1;

export const isExponentialFormat = (text: string | undefined) => {
  if (!text) {
    return false;
  }
  return ValidationPatterns.EXPONENTIAL_NUMBER.test(text);
};

export type IntervalTimer = ReturnType<typeof setInterval>;

export const sumAsBigNumber = (values: (number | undefined)[]): number => {
  const bigSum = values.reduce<BigNumber>((acc, x) => {
    if (x == null) return acc;
    return acc.plus(x);
  }, BigNumber(0));
  return bigSum.toNumber();
};

export const convertToNumber = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  switch (typeof value) {
    case 'number':
      return value;
    case 'string': {
      if (value.trim().length == 0) return undefined;
      const numberValue = Number(value);
      return isNaN(numberValue) ? undefined : numberValue;
    }
    case 'object': {
      if (BigNumber.isBigNumber(value)) {
        return value.toNumber();
      }
      return undefined;
    }
    default:
      return undefined;
  }
};
